import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminRezliveSchedulerSettings = () => {
  const navigate = useNavigate();
  const [hotelLoading, setHotelLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  const handleRunHotelScheduler = async () => {
    try {
      setHotelLoading(true);
      toast.info('Starting Hotel Sync Scheduler... this may take a few minutes.');
      const response = await adminApiService.runRezliveScheduler();
      if (response.success) {
        toast.success(response.message || 'Hotel Sync completed successfully');
      } else {
        toast.error(response.message || 'Failed to run Hotel Sync');
      }
    } catch (error) {
      console.error('Error running hotel scheduler:', error);
      toast.error('An error occurred while running the hotel scheduler');
    } finally {
      setHotelLoading(false);
    }
  };

  const handleRunCityScheduler = async () => {
    try {
      setCityLoading(true);
      toast.info('Starting City Import Scheduler... building search index may take a while.');
      const response = await adminApiService.runRezliveCityScheduler();
      if (response.success) {
        toast.success(response.message || 'City Import completed successfully');
      } else {
        toast.error(response.message || 'Failed to run City Import');
      }
    } catch (error) {
      console.error('Error running city scheduler:', error);
      toast.error('An error occurred while running the city scheduler');
    } finally {
      setCityLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center">
        <button 
          onClick={() => navigate('/admin/dashboard/settings')}
          className="mr-4 p-2 text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RezLive Schedulers</h1>
          <p className="text-gray-600 mt-2">Manage backend data synchronization for hotels and cities</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Hotel Scheduler Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Hotel Mapping Sync</h3>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Imports hotels from the configured CSV file. Uses the <strong>hotelCode</strong> to prevent duplicates.
              If a hotel already exists, it updates its details; otherwise, it inserts a new record.
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded mb-6 border border-gray-100 font-mono overflow-x-auto">
              File: rezlive.hotels.file.path
            </div>
          </div>
          <button 
            onClick={handleRunHotelScheduler}
            disabled={hotelLoading || cityLoading}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {hotelLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing Hotels...
              </>
            ) : (
              'Run Hotel Sync'
            )}
          </button>
        </div>

        {/* City Scheduler Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">City Import Sync</h3>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Advanced syncing of cities and countries from CSVs. Maps RezLive cities to your internal database using Exact, Phonetic, and Fuzzy matching algorithms to link IDs correctly.
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded mb-6 border border-gray-100 font-mono overflow-x-auto">
              <div>File 1: rezlive.countries.file.path</div>
              <div className="mt-1">File 2: rezlive.cities.file.path</div>
            </div>
          </div>
          <button 
            onClick={handleRunCityScheduler}
            disabled={hotelLoading || cityLoading}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cityLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Importing Cities...
              </>
            ) : (
              'Run City Import'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AdminRezliveSchedulerSettings;
